import React from "react";
import {
  Card,
  CardBody,
  Container,
  Row,
  Col,
} from "reactstrap";
import { Link } from "react-router-dom";

import DemoNavbar from "components/Navbars/DemoNavbar.js";
import SimpleFooter from "components/Footers/SimpleFooter.js";
import { setPageMeta } from "utils/seo.js";

class DataAnalytics extends React.Component {
  componentDidMount() {
    setPageMeta({
      title: "Platform Analytics",
      description:
        "Real-time analytics, predictive modeling, and custom dashboards built into the Chisp IoT platform.",
      path: "/data-analytics",
    });
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
    this.refs.main.scrollTop = 0;
  }

  render() {
    return (
      <>
        <DemoNavbar />
        <main ref="main">
          <div className="position-relative">
            <section className="section section-lg section-shaped pb-250">
              <div className="shape shape-style-1 shape-chisp-analytics">
                <span />
                <span />
                <span />
                <span />
                <span />
                <span />
                <span />
                <span />
                <span />
              </div>
              <Container className="py-lg-md d-flex">
                <div className="col px-0">
                  <Row>
                    <Col lg="10" className="mx-auto text-center">
                      <h1 className="display-3 text-white">
                        Platform{" "}
                        <span>Analytics</span>
                      </h1>
                      <p className="lead text-white">
                        The analytics layer built into the Chisp IoT platform: real-time processing,
                        predictive modeling, and dashboards over your device and sensor data.
                        Looking for analytics on your own data stack instead? That is our{" "}
                        <Link to="/consulting" className="text-white font-weight-bold">
                          data consulting practice
                        </Link>
                        .
                      </p>
                    </Col>
                  </Row>
                </div>
              </Container>
            </section>
          </div>

          <section className="section section-lg pt-lg-0 mt--200">
            <Container>
              <Row className="justify-content-center">
                <Col lg="12">
                  <Row className="row-grid">
                    <Col lg="4">
                      <Card className="shadow border-0 h-100">
                        <CardBody className="py-5">
                          <div className="icon icon-shape icon-shape-info rounded-circle mb-4">
                            <i className="fa fa-line-chart" />
                          </div>
                          <h5 className="text-info">Real-time Analytics</h5>
                          <p className="description mt-3">
                            Process and analyze data streams in real-time with sub-second latency.
                            Monitor KPIs, detect anomalies, and respond to changes instantly.
                          </p>
                          <ul className="list-unstyled mt-3">
                            <li className="py-1">
                              <i className="fa fa-check text-success mr-2" />
                              Live data visualization
                            </li>
                            <li className="py-1">
                              <i className="fa fa-check text-success mr-2" />
                              Anomaly detection
                            </li>
                            <li className="py-1">
                              <i className="fa fa-check text-success mr-2" />
                              Automated alerting
                            </li>
                          </ul>
                        </CardBody>
                      </Card>
                    </Col>
                    <Col lg="4">
                      <Card className="shadow border-0 h-100">
                        <CardBody className="py-5">
                          <div className="icon icon-shape icon-shape-primary rounded-circle mb-4">
                            <i className="fa fa-lightbulb-o" />
                          </div>
                          <h5 className="text-primary">Predictive Modeling</h5>
                          <p className="description mt-3">
                            Leverage machine learning to predict future trends, identify risks,
                            and optimize operations before issues occur.
                          </p>
                          <ul className="list-unstyled mt-3">
                            <li className="py-1">
                              <i className="fa fa-check text-success mr-2" />
                              Trend forecasting
                            </li>
                            <li className="py-1">
                              <i className="fa fa-check text-success mr-2" />
                              Risk assessment
                            </li>
                            <li className="py-1">
                              <i className="fa fa-check text-success mr-2" />
                              Performance optimization
                            </li>
                          </ul>
                        </CardBody>
                      </Card>
                    </Col>
                    <Col lg="4">
                      <Card className="shadow border-0 h-100">
                        <CardBody className="py-5">
                          <div className="icon icon-shape icon-shape-success rounded-circle mb-4">
                            <i className="fa fa-tachometer" />
                          </div>
                          <h5 className="text-success">Custom Dashboards</h5>
                          <p className="description mt-3">
                            Create personalized dashboards with drag-and-drop widgets,
                            custom charts, and role-based access controls.
                          </p>
                          <ul className="list-unstyled mt-3">
                            <li className="py-1">
                              <i className="fa fa-check text-success mr-2" />
                              Drag-and-drop builder
                            </li>
                            <li className="py-1">
                              <i className="fa fa-check text-success mr-2" />
                              Interactive visualizations
                            </li>
                            <li className="py-1">
                              <i className="fa fa-check text-success mr-2" />
                              Role-based access
                            </li>
                          </ul>
                        </CardBody>
                      </Card>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Container>
          </section>

          <section className="section section-lg bg-gradient-default">
            <Container>
              <Row className="justify-content-center text-center mb-lg">
                <Col lg="8">
                  <h2 className="display-3 text-white">Analytics Capabilities</h2>
                  <p className="lead text-white">
                    Comprehensive analytics tools designed to handle data from any source
                    and provide insights that drive business value.
                  </p>
                </Col>
              </Row>
              <Row className="row-grid">
                <Col lg="3" md="6">
                  <div className="text-center">
                    <div className="icon icon-lg icon-shape bg-gradient-white shadow rounded-circle text-primary mb-4">
                      <i className="fa fa-database text-primary" />
                    </div>
                    <h5 className="text-white">Data Integration</h5>
                    <p className="text-white">
                      Connect to databases, APIs, IoT devices, and cloud platforms.
                      Unified data pipeline for comprehensive analysis.
                    </p>
                  </div>
                </Col>
                <Col lg="3" md="6">
                  <div className="text-center">
                    <div className="icon icon-lg icon-shape bg-gradient-white shadow rounded-circle text-primary mb-4">
                      <i className="fa fa-pie-chart text-primary" />
                    </div>
                    <h5 className="text-white">Advanced Visualization</h5>
                    <p className="text-white">
                      Interactive charts, graphs, and maps with drill-down capabilities.
                      Share insights with stakeholders through rich visualizations.
                    </p>
                  </div>
                </Col>
                <Col lg="3" md="6">
                  <div className="text-center">
                    <div className="icon icon-lg icon-shape bg-gradient-white shadow rounded-circle text-primary mb-4">
                      <i className="fa fa-cogs text-primary" />
                    </div>
                    <h5 className="text-white">AI-Powered Insights</h5>
                    <p className="text-white">
                      Automated pattern recognition, natural language processing,
                      and intelligent recommendations powered by AI.
                    </p>
                  </div>
                </Col>
                <Col lg="3" md="6">
                  <div className="text-center">
                    <div className="icon icon-lg icon-shape bg-gradient-white shadow rounded-circle text-primary mb-4">
                      <i className="fa fa-shield text-primary" />
                    </div>
                    <h5 className="text-white">Data Security</h5>
                    <p className="text-white">
                      Enterprise-grade security with encryption, access controls,
                      and compliance with data protection regulations.
                    </p>
                  </div>
                </Col>
              </Row>
            </Container>
          </section>

          <section className="section section-lg">
            <Container>
              <Row className="justify-content-center text-center mb-lg">
                <Col lg="8">
                  <h2 className="display-3">Industry Applications</h2>
                  <p className="lead text-muted">
                    Our data analytics solutions are trusted across industries
                    to solve complex business challenges.
                  </p>
                </Col>
              </Row>
              <Row className="row-grid">
                <Col lg="4">
                  <Card className="shadow border-0">
                    <CardBody className="px-lg-5 py-lg-5">
                      <div className="text-center text-muted mb-4">
                        <i className="fa fa-industry fa-3x" />
                      </div>
                      <h6 className="text-primary text-uppercase">Manufacturing</h6>
                      <p className="description mt-3">
                        Optimize production lines, predict equipment failures,
                        and improve quality control through data-driven insights.
                      </p>
                    </CardBody>
                  </Card>
                </Col>
                <Col lg="4">
                  <Card className="shadow border-0">
                    <CardBody className="px-lg-5 py-lg-5">
                      <div className="text-center text-muted mb-4">
                        <i className="fa fa-bar-chart fa-3x" />
                      </div>
                      <h6 className="text-info text-uppercase">Business Intelligence</h6>
                      <p className="description mt-3">
                        Transform business data into strategic insights for better
                        decision-making and competitive advantage.
                      </p>
                    </CardBody>
                  </Card>
                </Col>
                <Col lg="4">
                  <Card className="shadow border-0">
                    <CardBody className="px-lg-5 py-lg-5">
                      <div className="text-center text-muted mb-4">
                        <i className="fa fa-sitemap fa-3x" />
                      </div>
                      <h6 className="text-success text-uppercase">IoT Analytics</h6>
                      <p className="description mt-3">
                        Analyze sensor data, monitor device performance,
                        and optimize IoT deployments across any scale.
                      </p>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </Container>
          </section>

          <section className="section section-lg bg-gradient-default">
            <Container>
              <Row className="text-center justify-content-center">
                <Col lg="10">
                  <h2 className="display-3 text-white">Get Started Today</h2>
                  <p className="lead text-white">
                    Transform your data into competitive advantage with our comprehensive analytics platform.
                  </p>
                </Col>
              </Row>
              <Row className="row-grid mt-5">
                <Col lg="4">
                  <div className="text-center">
                    <div className="icon icon-lg icon-shape bg-gradient-white shadow rounded-circle text-primary mb-4">
                      <i className="fa fa-play text-primary" />
                    </div>
                    <h4 className="text-white mt-3">Quick Start</h4>
                    <p className="text-white mt-3">
                      Get up and running in minutes with our intuitive setup process
                      and pre-built templates.
                    </p>
                  </div>
                </Col>
                <Col lg="4">
                  <div className="text-center">
                    <div className="icon icon-lg icon-shape bg-gradient-white shadow rounded-circle text-primary mb-4">
                      <i className="fa fa-users text-primary" />
                    </div>
                    <h4 className="text-white mt-3">Expert Support</h4>
                    <p className="text-white mt-3">
                      Our team of data scientists and engineers are here to help
                      you maximize the value of your data.
                    </p>
                  </div>
                </Col>
                <Col lg="4">
                  <div className="text-center">
                    <div className="icon icon-lg icon-shape bg-gradient-white shadow rounded-circle text-primary mb-4">
                      <i className="fa fa-rocket text-primary" />
                    </div>
                    <h4 className="text-white mt-3">Scalable Platform</h4>
                    <p className="text-white mt-3">
                      From startup to enterprise, our platform grows with your
                      data needs and organizational requirements.
                    </p>
                  </div>
                </Col>
              </Row>
            </Container>
          </section>
        </main>
        <SimpleFooter />
      </>
    );
  }
}

export default DataAnalytics;
